import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useRedirectUrl } from "../../../../../hooks/Authorize";
import { rememberRedirectUrl, resolveRedirectUrl } from "../../../../../utilities/authRedirect";
import GatewayShell from "../../../../../gateway/GatewayShell";
import { getDestinationContext } from "../../../../../gateway/destinationEngine";
import GatewayIdentityCard from "../../../../../gateway/GatewayIdentityCard";
import { clearRememberedIdentity, getRememberedIdentity, identityUsername } from "../../../../../gateway/gatewayIdentity";
import GatewayAction from "../../../../../gateway/GatewayAction";
import GatewayIdentityField from "../../../../../gateway/GatewayIdentityField";
import { getCookie } from "../../../../../utilities/cookies";
import { getIdentityTransaction } from "../../../../../gateway/identityTransaction";

const Login = () => {
  const navigate = useNavigate();
  const redirectUrl = useRedirectUrl();
  const rememberedIdentity = useMemo(() => getRememberedIdentity(), []);
  const authenticatedSession = getCookie("user");
  const [recognitionMode, setRecognitionMode] = useState(Boolean(rememberedIdentity));
  const [gatewayOpen, setGatewayOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const continueWithIdentity = async (username) => {
    setSubmitting(true); setErrorMessage("");
    const transaction = getIdentityTransaction() || { clientId: "accounts", protocolVersion: "2", redirectUri: "" };
    const formData = new FormData();
    formData.append("email", `${username}@clikkmail.com`);
    formData.append("client_id", transaction.clientId);
    formData.append("identity_protocol_version", transaction.protocolVersion);
    if (transaction.redirectUri) formData.append("redirect_uri", transaction.redirectUri);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/login_email`, { method: "POST", body: formData });
      const responseData = await response.json();
      if (!response.ok || !responseData.success) {
        const detail = responseData.correlationId ? ` (${responseData.correlationId})` : "";
        throw new Error(`${responseData.message || responseData.error || "We couldn't verify that Clikkle ID."}${detail}`);
      }
      localStorage.setItem("formData", JSON.stringify({ email: username }));
      rememberRedirectUrl(redirectUrl);
      navigate("/login_2");
    } catch (error) {
      setErrorMessage(error.message); toast.error(error.message);
    } finally { setSubmitting(false); }
  };

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({ email: Yup.string().matches(/^[a-zA-Z0-9._-]+$/, "Use letters, numbers, dots, dashes or underscores").min(2).max(64).required("Enter your Clikkle ID") }),
    onSubmit: ({ email }) => continueWithIdentity(email),
  });

  const referralApp = getIdentityTransaction()?.referralApp || "";
  const destination = getDestinationContext({ redirectUrl, referralApp });
  if (recognitionMode && rememberedIdentity) {
    return (
      <GatewayShell stage="identity-recognition" destinationLabel={destination.continuingLabel} arrival open={gatewayOpen} onOpen={() => setGatewayOpen(true)} onBack={() => setGatewayOpen(false)}>
        <section className="gatewayCard" aria-labelledby="recognized-title">
          <div className="gatewayCard__icon" aria-hidden="true">✓</div>
          <h2 id="recognized-title">Welcome back</h2>
          <p className="gatewayCard__sub">Continue with your recognized Clikkle identity.</p>
          <GatewayIdentityCard identity={rememberedIdentity} />
          {errorMessage ? <div className="gatewayError" role="alert">{errorMessage}</div> : null}
          <GatewayAction type="button" disabled={submitting} onClick={() => continueWithIdentity(identityUsername(rememberedIdentity))}>{submitting ? "Checking identity…" : `Continue as ${rememberedIdentity.displayName}`}</GatewayAction>
          {authenticatedSession?.success && (authenticatedSession?.token || authenticatedSession?.sessionToken) ? <GatewayAction variant="secondary" type="button" onClick={() => navigate("/security-center")}>Security center</GatewayAction> : null}
          <GatewayAction variant="secondary" type="button" onClick={() => { clearRememberedIdentity(); setRecognitionMode(false); }}>Use another account</GatewayAction>
        </section>
      </GatewayShell>
    );
  }

  return (
    <GatewayShell stage="identity-entry" destinationLabel={destination.continuingLabel} arrival open={gatewayOpen} onOpen={() => setGatewayOpen(true)} onBack={() => setGatewayOpen(false)}>
      <form className="gatewayCard" onSubmit={formik.handleSubmit} noValidate>
        <div className="gatewayCard__icon" aria-hidden="true">✦</div>
        <h2>Welcome to Clikkle</h2>
        <p className="gatewayCard__sub">Use your Clikkle ID to continue securely.</p>
        <GatewayIdentityField
          autoFocus
          autoComplete="username"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Your Clikkle ID"
          error={(formik.touched.email && formik.errors.email) || errorMessage}
        />
        <GatewayAction type="submit" disabled={submitting}>{submitting ? "Checking identity…" : "Continue"}</GatewayAction>
        <div className="gatewayActions"><Link to="/forgot-mail-email">Forgot your ID?</Link><Link to="/register">Create account</Link></div>
      </form>
    </GatewayShell>
  );
};
export default Login;
